import type {
  LineProfileResponse,
  LineTokenRequest,
  LineTokenResponse,
  MemberAccountDetailsResponse,
  MemberAccountUpdateRequest,
  MemberAddressResponse,
  MemberBillingAddressRequest,
  MemberBillingAddressResponse,
  MemberRequest,
  MemberShippingAddressRequest,
  MemberShippingAddressResponse,
  UpdateLineIdToMemberRequest,
  UpdateLineIdToMemberResponse,
} from "@features/authentication/types";

import { api } from "@services/api";

export class MemberService {
  private readonly basePath = "/member";

  // Create billing address method
  public async createBillingAddress(
    data: MemberBillingAddressRequest,
  ): Promise<MemberBillingAddressResponse> {
    return await api.post<MemberBillingAddressResponse>(
      `${this.basePath}/address/billing/create`,
      data,
    );
  }

  // Create shipping address method
  public async createShippingAddress(
    data: MemberShippingAddressRequest,
  ): Promise<MemberShippingAddressResponse> {
    return await api.post<MemberShippingAddressResponse>(
      `${this.basePath}/address/shipping/create`,
      data,
    );
  }

  // Get account details method
  public async getAccountDetails(memberId: string): Promise<MemberAccountDetailsResponse> {
    const request: MemberRequest = { member_id: memberId };
    return await api.post<MemberAccountDetailsResponse>(`${this.basePath}/account/detail`, request);
  }

  // Get address method
  public async getAddress(memberId: string): Promise<MemberAddressResponse> {
    const request: MemberRequest = { member_id: memberId };
    return await api.post<MemberAddressResponse>(`${this.basePath}/address`, request);
  }

  // Line get Token
  public async lineGetToken(data: LineTokenRequest): Promise<LineTokenResponse> {
    return await api.post<LineTokenResponse>(`https://api.line.me/oauth2/v2.1/token`, data, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  }

  // Line get User Profile
  public async lineGetUserProfile(accessToken: string): Promise<LineProfileResponse> {
    return await api.get<LineProfileResponse>(`https://api.line.me/v2/profile`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  // Update account details method
  public async updateAccountDetails(
    data: MemberAccountUpdateRequest,
  ): Promise<MemberAccountDetailsResponse> {
    return await api.post<MemberAccountDetailsResponse>(`${this.basePath}/account/update`, data);
  }

  // Update billing address method
  public async updateBillingAddress(
    data: MemberBillingAddressRequest,
  ): Promise<MemberBillingAddressResponse> {
    return await api.post<MemberBillingAddressResponse>(
      `${this.basePath}/address/billing/update`,
      data,
    );
  }

  // Update line id binding to member
  public async updateLineIdToMember(
    data: UpdateLineIdToMemberRequest,
  ): Promise<UpdateLineIdToMemberResponse> {
    return await api.post<UpdateLineIdToMemberResponse>(
      `${this.basePath}/account/line/binding`,
      data,
    );
  }

  // Update shipping address method
  public async updateShippingAddress(
    data: MemberShippingAddressRequest,
  ): Promise<MemberShippingAddressResponse> {
    return await api.post<MemberShippingAddressResponse>(
      `${this.basePath}/address/shipping/update`,
      data,
    );
  }

  // Validate line id binding to member
  public async validateLineIdToMember(
    data: UpdateLineIdToMemberRequest,
  ): Promise<UpdateLineIdToMemberResponse> {
    return await api.post<UpdateLineIdToMemberResponse>(
      `${this.basePath}/account/line/validate/binding`,
      data,
    );
  }
}

export const memberService = new MemberService();
